import React from "react";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import AppTextField from "../../lib/components/AppTextField";
import { useNavigate, useParams } from "react-router-dom";
import AppSelectField from "../../lib/components/AppSelectField";
import { Button, IconButton, InputAdornment } from "@mui/material";
import { Add, ArrowBack, Search } from "@mui/icons-material";
import { useMutation, useQuery } from "@tanstack/react-query";
import organizationservice from "../../service/organizationservice";
import PreloaderTable from "../../lib/components/PreloaderTable";
import AdministratorsDataTable from "./components/administrators-data-table";
import scrollHandler from "../../utils/scrollHandler";
import BTNLoader from "../../lib/components/BTNLoader";
import AddNewAdminUser from "./components/add-new-adminuser";
import errorHandler from "../../utils/errorHandler";

const OrganizationAdministrators = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const organizationId = searchParams.get("organizationId") ?? "";
  const limit = searchParams.get("limit") || "20";
  const keyword = searchParams.get("keyword") || "";
  const status = searchParams.get("status") || "";
  const [isAddNew, setIsAddNew] = React.useState(false);

  const qKeys = [page, limit, status];
  const queries = { page, limit, keyword, status, organizationId: id };

  const organizationQuery = useQuery({
    retry: false,
    queryKey: ["organizations-details", organizationId],
    queryFn: () => organizationservice.getOne({ id: organizationId }),
  });

  const usersQuery = useQuery({
    retry: false,
    queryKey: ["organizations-administrators", ...qKeys],
    queryFn: () => organizationservice.getAdministrators(queries),
  });

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      usersQuery.refetch();
    }, 1000);
    return () => clearTimeout(timeoutId);
    //eslint-disable-next-line
  }, [keyword]);

  const { isPending: isLoading, data } = usersQuery;
  const usersData = data?.data;
  const organization = organizationQuery?.data?.data?.data;

  return (
    <AppLayout>
      <Header
        title="Administrators"
        description="Get insights to accounts on Jureb here"
      />

      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <IconButton
          className="bg-secondary-subtle mb-3"
          onClick={() => navigate(-1)}
        >
          <ArrowBack />
        </IconButton>
        <div className="d-flex justify-content-between mb-5">
          <div>
            <div className="fs-4 fw-medium">{organization?.name}</div>
            <div className="text-secondary fs-sm">
              Get insights to accounts on jureb here
            </div>
          </div>
          <BTNLoader isLoading={organizationQuery.isPending} />
        </div>
        <div className="row mb-3">
          <div className="col-6 col-md-3 col-lg-4 mb-3">
            <div>
              <AppTextField
                placeholder="Search administrators..."
                value={keyword}
                onChange={(e) => handleChange(e, "keyword")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search className="text-secondary" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>

          <div className="col-6 col-md-3 col-lg-2 mb-3">
            <div>
              <AppSelectField
                value={status}
                placeholder="Status"
                onChange={(e) => handleChange(e, "status")}
                items={[
                  { label: "All", value: "ALL" },
                  { label: "Active", value: "ACTIVE" },
                  { label: "Inactive", value: "INACTIVE" },
                ]}
              />
            </div>
          </div>
          <div className="col-6 col-md-3 col-lg-6 mb-3">
            <div className="d-flex justify-content-end">
              <Button
                onClick={() => setIsAddNew(true)}
                endIcon={<Add />}
                variant="contained"
                color="secondary"
              >
                Add New
              </Button>
            </div>
          </div>
        </div>
        <PreloaderTable isLoading={isLoading} />
        <AdministratorsDataTable
          isLoading={isLoading}
          data={usersData?.data}
          usersQuery={usersQuery}
          page={Number(page)}
          pageSize={Number(limit)}
          totalPages={usersData?.totalPages}
          onPageChange={(value) => handleChange(value, "page")}
          onPageSizeChange={(value) =>
            handleChange({ target: { value } }, "limit")
          }
        />
      </div>
      <AddNewAdminUser
        isOpen={isAddNew}
        organizationId={organizationId}
        onClose={() => setIsAddNew(false)}
        refetch={() => usersQuery.refetch()}
      />
    </AppLayout>
  );

  function handleChange(e, name = "") {
    const value = name === "page" ? e : e.target.value;
    searchParams.set(name, value);
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
    );
    if (name === "page") scrollHandler();
  }
};

export default OrganizationAdministrators;
